import type { ZodTypeAny } from 'zod'
import z from 'zod'

export const ErrorResponseSchema = z.object({
  message: z.union([z.string(), z.array(z.string())]),
  error: z.unknown().optional(),
  statusCode: z.number().optional(),
  type: z.string().optional(),
  // Distinct `type` values collected from the entire cause chain of the
  // underlying error (e.g. ApplicationFailure types like 'DogNotPuppy' nested
  // inside a WorkflowFailedError). Populated by callers that walk the chain.
  causeTypes: z.array(z.string()).optional(),
  // Distinct `message` values collected from the same cause chain as
  // `causeTypes`, at every nesting level.
  causeMessages: z.array(z.string()).optional(),
  details: z.unknown().optional(),
})

export type ErrorResponse = z.infer<typeof ErrorResponseSchema>

/** Workflow metadata returned on synchronous orchestration workflow failures only. */
export const OrchestrationWorkflowErrorResponseSchema = ErrorResponseSchema.extend({
  workflowType: z.string().optional(),
  workflowId: z.string().optional(),
  workflowRunId: z.string().optional(),
})

export const Identifier = z.string().uuid()

export type Identifiable<T extends object> = T & {
  id: z.infer<typeof Identifier>
}

export const PaginatedResultsQuerySchema = z.object({
  offset: z.coerce.number().int().nonnegative().optional(),
  limit: z.coerce.number().int().nonnegative().max(100).optional(),
})

export type PaginatedResultsQuery = z.infer<typeof PaginatedResultsQuerySchema>

export const generateResultsWithOptionalPagination = <T extends ZodTypeAny>(data: T) => {
  return z.object({
    data: data.array(),
    meta: z.object({
      total: z.number().int().nonnegative(),
      offset: z.number().int().nonnegative(),
      limit: z.number().int().nonnegative(),
    }).nullable().optional(),
  })
}

export const generateRequiredPaginationQuery = (defaultMax = 1000) => {
  return z.object({
    offset: z.coerce.number().int().nonnegative(),
    limit: z.coerce.number().int().nonnegative().max(defaultMax),
  })
}

export const generateRequiredPaginationResult = <T extends ZodTypeAny>(data: T) => {
  return z.object({
    data: data.array(),
    meta: z.object({
      total: z.number().int().nonnegative(),
      offset: z.number().int().nonnegative(),
      limit: z.number().int().nonnegative(),
    }),
  })
}

/**
 * Reusable sort query parameter for paginated list endpoints (Google AIP-132 style).
 *
 * Format: comma-separated `column [direction]` entries — direction is optional
 * and defaults to `asc`. Matching is case-insensitive and whitespace around
 * commas is tolerated.
 * Examples: `?sort=deactivatedAt asc,id desc`, `?sort=disabled asc, name`, `?sort=name`
 *
 * Spaces are URL-encoded automatically by ts-rest / fetch clients.
 */

export type SortDirection = 'asc' | 'desc'

export interface ParsedSortEntry<T extends string = string> {
  column: T
  direction: Uppercase<SortDirection>
}

export function createSortQuerySchema<const T extends readonly [string, ...string[]]>(allowedColumns: T) {
  const columnPattern = allowedColumns.join('|')
  const pattern = new RegExp(`^\\s*(${columnPattern})( +(asc|desc))?(\\s*,\\s*(${columnPattern})( +(asc|desc))?)*\\s*$`, 'i')
  return z.string().regex(pattern, {
    message: `Sort must be comma-separated "column [direction]" entries. Allowed columns: ${allowedColumns.join(', ')}. Directions: asc, desc (default asc).`,
  }).optional()
}

export function parseSortQuery<const T extends readonly string[]>(
  sort: string | undefined,
  allowedColumns: T,
): ParsedSortEntry<T[number]>[] {
  if (!sort)
    return []
  return sort.split(',').map((entry) => {
    const [column = '', direction = ''] = entry.trim().split(/\s+/)
    const matchedColumn = allowedColumns.find(allowed => allowed.toLowerCase() === column.toLowerCase())
    if (!matchedColumn) {
      throw new Error(`Invalid sort column: ${column}`)
    }

    return {
      column: matchedColumn as T[number],
      direction: (direction || 'asc').toUpperCase() as Uppercase<SortDirection>,
    }
  })
}

/**
 * Reusable Zod schema for a `facilityIds` query parameter that accepts the
 * three serialised forms ts-rest may produce on the wire:
 *   - single value: "2"
 *   - repeated keys: ["2", "3"]
 *   - bracket-notation object: { "0": "2", "1": "3" }
 */
export const FacilityIdsQueryParamSchema = z.preprocess(
  (val) => {
    if (typeof val === 'string')
      return [Number(val)]
    if (Array.isArray(val))
      return val.map(Number)
    if (val && typeof val === 'object' && !Array.isArray(val))
      return Object.values(val).map(Number)
    return val
  },
  z.array(z.number().int().positive()).min(1).describe('Facility IDs to filter by'),
)
